import React, { useEffect, useState } from 'react';
import { Table, Spin, message } from 'antd';
import axios from '../../../config/axiosInstance';
import { useTheme } from '../../../context/ThemeContext';

const ProgrammeBookingsTab = ({ participantId, host, programmePoints }) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const { darkTheme } = useTheme();

    const fetchProgrammeBookings = async () => {
        try {
            const tokenRes = await axios.get(`/regasus/token?host=${host}`);
            const token = tokenRes.data.access_token;

            if (!token) {
                message.error('No token received');
                return;
            }

            const res = await axios.post('/regasus/get-programme-bookings-of-participant', {
                token,
                host,
                recipientID: participantId
            });

            setBookings(res.data?.bookings || []);
        } catch (err) {
            console.error(err);
            message.error('Failed to load programme bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProgrammeBookings();
    }, [participantId, host]);

    const getProgrammePointName = (id) => {
        return programmePoints?.find(p => p.id === id)?.name?.[0]?.value || id;
    };

    const columns = [
        {
            title: 'Programme Point',
            dataIndex: 'programmePointID',
            key: 'programmePointID',
            render: (value) => getProgrammePointName(value),
        },
        {
            title: 'Booking Date',
            dataIndex: 'bookingDate',
            key: 'bookingDate',
            render: (value) => value ? new Date(value).toLocaleString() : '—',
        },
        {
            title: 'Benefit Recipient',
            dataIndex: 'benefitRecipientName',
            key: 'benefitRecipientName',
            render: (value) => value || '—',
        },
        {
            title: 'Invoice Recipient',
            dataIndex: 'invoiceRecipientName',
            key: 'invoiceRecipientName',
            render: (value) => value || '—',
        },
        {
            title: 'Amount',
            key: 'amountBrutto',
            render: (_, record) => {
                const amount = record?.price?.amountBrutto;
                const currency = record?.price?.currency;
                return amount ? `${amount.toFixed(2)} ${currency}` : '—';
            }
        },
        {
            title: 'Open Amount',
            key: 'openAmount',
            render: (_, record) => {
                const open = record?.openAmount ?? null;
                const amount = record?.price?.amountBrutto ?? null;
                const currency = record?.price?.currency || '';

                if (open === null || amount === null) return '—';

                const isPaidPartially = open < amount;

                const color = isPaidPartially
                    ? (darkTheme ? '#52c41a' : 'green')
                    : (darkTheme ? '#ff7875' : 'red');

                return (
                    <span style={{ color }}>
                        {open.toFixed(2)} {currency}
                    </span>
                );
            }
        }
    ];

    return loading ? (
        <Spin />
    ) : (
        <Table
            size="small"
            scroll={{ x: 'max-content' }}
            dataSource={bookings
                .filter(b => {
                    const amount = b?.price?.amountBrutto || 0;
                    const open = b?.openAmount || 0;
                    return amount !== 0 || open !== 0;
                })
                .map((b, i) => ({ key: b.id || i, ...b }))
            }
            columns={columns}
            pagination={{ pageSize: 10 }}
            summary={pageData => {
                const totalAmount = pageData.reduce((sum, record) => sum + (record?.price?.amountBrutto || 0), 0);
                const totalOpen = pageData.reduce((sum, record) => sum + (record?.openAmount || 0), 0);
                const currency = pageData[0]?.price?.currency || '';

                return (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4}>
                            <strong style={{ fontSize: '18px' }}>Total</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                            <strong style={{ fontSize: '18px' }}>
                                {totalAmount.toFixed(2)} {currency}
                            </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                            <strong style={{ fontSize: '18px' }}>
                                {totalOpen.toFixed(2)} {currency}
                            </strong>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                );
            }}
        />
    );
};

export default ProgrammeBookingsTab;